/**
 * WordsBadge — web port of client-native/src/ui/molecules/WordsBadge.tsx.
 *
 * A small pill showing "📖 {count} {label}". Metrics match iOS exactly:
 * height 26, radius 12 (--radius-badge), paddingHorizontal 6, content gap 2,
 * xs/semibold text, --shadow-badge. Two themes (onLight / onDark) mirror the
 * iOS themeStyles. Bounces once when the count increments (iOS spring →
 * framer-motion keyframes).
 */
import { FC, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { colors } from "./colors";
import { fontVar, fontWeight } from "./tokens";

export interface WordsBadgeProps {
  count: number;
  theme?: "onLight" | "onDark";
  /**
   * Label length, mirrors iOS WordsBadge `variant`. "short" collapses to a
   * single word ("known" / "words"); "long" spells out the noun
   * ("word known" / "words known", "word" / "words"). Defaults to "short".
   */
  variant?: "short" | "long";
  /** Whether the count represents *known* words (changes the label copy). */
  known?: boolean;
  onClick?: () => void;
}

const BADGE_HEIGHT = 26;
const BADGE_PADDING_HORIZONTAL = 6;

const themeStyles = {
  onLight: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.greyLightest}`,
    textColor: colors.text.primary,
  },
  onDark: {
    backgroundColor: colors.whiteMedium,
    border: "none",
    textColor: colors.white,
  },
} as const;

/** >=1000 -> "{x.x}k", else the raw integer. Mirrors iOS formatNumber. */
const formatNumber = (num: number): string => {
  if (num >= 1000) return (num / 1000).toFixed(1) + "k";
  return num.toString();
};

// Label matrix mirrors iOS WordsBadge (client-native/src/ui/molecules/WordsBadge.tsx:93-103).
const resolveLabel = (
  count: number,
  known: boolean,
  variant: "short" | "long"
): string => {
  if (known) {
    if (variant === "short") return "known";
    return count === 1 ? "word known" : "words known";
  }
  if (variant === "short") return "words";
  return count === 1 ? "word" : "words";
};

const WordsBadge: FC<WordsBadgeProps> = ({
  count,
  theme = "onLight",
  variant = "short",
  known = false,
  onClick,
}) => {
  const t = themeStyles[theme];
  const previousCount = useRef(count);
  const [bounceKey, setBounceKey] = useState(0);

  useEffect(() => {
    if (count > previousCount.current) {
      // Re-key the motion element to replay the bounce keyframes on increment.
      setBounceKey((k) => k + 1);
    }
    previousCount.current = count;
  }, [count]);

  const interactive = typeof onClick === "function";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      // bounce: 1 -> 1.08 (spring-ish) -> 1, matches iOS Animated.sequence
      animate={
        bounceKey > 0
          ? { scale: [1, 1.08, 1] }
          : undefined
      }
      transition={{ duration: 0.35, times: [0, 0.35, 1], ease: "easeOut" }}
      whileTap={interactive ? { scale: 0.95 } : undefined}
      key={bounceKey}
      aria-label={`📖 ${formatNumber(count)} ${resolveLabel(count, known, variant)}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        height: BADGE_HEIGHT,
        paddingLeft: BADGE_PADDING_HORIZONTAL,
        paddingRight: BADGE_PADDING_HORIZONTAL,
        borderRadius: "var(--radius-badge)",
        backgroundColor: t.backgroundColor,
        border: t.border,
        boxShadow: "var(--shadow-badge)",
        color: t.textColor,
        fontSize: fontVar("xs"),
        fontWeight: fontWeight.semibold,
        lineHeight: 1,
        cursor: interactive ? "pointer" : "default",
        boxSizing: "border-box",
        // strip native button chrome but keep the centered pill look
        background: t.backgroundColor,
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span aria-hidden>📖</span>
      <span>{formatNumber(count)}</span>
      <span>{resolveLabel(count, known, variant)}</span>
    </motion.button>
  );
};

export default WordsBadge;
